import React from 'react';
import { useTranslation } from 'react-i18next';
import { Opportunity } from '../../types';
import { Target, TrendingUp, Euro } from 'lucide-react';

interface PipelineOverviewProps {
  opportunities: Opportunity[];
  onNavigate: (view: string) => void;
}

const PipelineOverview: React.FC<PipelineOverviewProps> = ({ opportunities, onNavigate }) => {
  const { t, i18n } = useTranslation('dashboard');
  const { t: to } = useTranslation('opportunities');

  const stages = {
    prospecting: { label: to('stages.qualification'), color: 'bg-bg-surface text-txt-secondary' },
    qualification: { label: to('stages.qualification'), color: 'bg-info-soft text-accent' },
    proposal: { label: to('stages.proposal'), color: 'bg-warning-soft text-warning' },
    negotiation: { label: to('stages.negotiation'), color: 'bg-warning-soft text-warning' },
    closing: { label: to('stages.negotiation'), color: 'bg-accent-soft text-accent' },
    won: { label: to('stages.closedWon'), color: 'bg-success text-txt-inverted' },
    lost: { label: to('stages.closedLost'), color: 'bg-danger-soft text-danger' }
  };

  const stageCounts = Object.fromEntries(
    Object.keys(stages).map(stage => [
      stage,
      opportunities.filter(o => o.stage === stage).length
    ])
  );

  const totalValue = opportunities
    .filter(o => !['won', 'lost'].includes(o.stage))
    .reduce((sum, o) => sum + (o.value || 0), 0);

  const formatCurrency = (amount: number) => {
    const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div 
      className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 cursor-pointer hover:shadow-card hover:scale-[1.02] transition-all duration-200"
      onClick={() => onNavigate('opportunities')}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-info-soft rounded-lg">
            <Target className="w-5 h-5 text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-txt">{t('pipeline.title')}</h3>
        </div>
        <div className="flex items-center space-x-1 text-sm">
          <Euro className="w-4 h-4 text-txt-secondary" />
          <span className="font-semibold text-txt">{formatCurrency(totalValue)}</span>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(stages).map(([key, stage]) => (
            <div
              key={key}
              className="flex items-center justify-between p-2 bg-bg-surface rounded-lg hover:bg-bg-surface transition-colors"
            >
              <span className="text-xs font-medium text-txt-secondary">{stage.label}</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${stage.color}`}>
                {stageCounts[key]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PipelineOverview;
